import { medplumResourcesApi, type UpdatePatientRequest } from './medplum-resources';
import { apiClient } from './client';
import type {
  PatientListItem,
  PatientRegistrationData,
  PatientUpdateData,
  PatientSearchParams,
  PaginatedPatientsResponse,
  PatientDetailResponse,
  PatientUpdateResponse,
  PatientDeletionResponse,
  Gender,
} from '@/lib/types/patient';

function normalizeString(value?: string | null): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function buildUpdatePatientRequest(
  data: PatientUpdateData | PatientRegistrationData
): UpdatePatientRequest {
  const request: UpdatePatientRequest = {};

  if ('firstName' in data && data.firstName !== undefined) {
    request.firstName = normalizeString(data.firstName);
  }
  if ('lastName' in data && data.lastName !== undefined) {
    request.lastName = normalizeString(data.lastName);
  }
  if ('dateOfBirth' in data && data.dateOfBirth !== undefined) {
    request.dateOfBirth = normalizeString(data.dateOfBirth);
  }
  if ('gender' in data && data.gender !== undefined) {
    request.gender = data.gender as Gender;
  }
  if ('email' in data && data.email !== undefined) {
    request.email = normalizeString(data.email) ?? null;
  }
  if ('phoneNumber' in data && data.phoneNumber !== undefined) {
    request.phone = normalizeString(data.phoneNumber) ?? null;
  }
  if ('address' in data && data.address !== undefined) {
    request.address = normalizeString(data.address);
  }
  if ('identifier' in data && (data as Partial<PatientUpdateData>).identifier !== undefined) {
    const identifierValue = normalizeString((data as Partial<PatientUpdateData>).identifier);
    request.identifier = identifierValue ?? '';
  }
  if ('city' in data && data.city !== undefined) {
    request.city = normalizeString(data.city);
  }
  if ('state' in data && data.state !== undefined) {
    request.state = normalizeString(data.state);
  }
  if ('postalCode' in data && data.postalCode !== undefined) {
    request.postalCode = normalizeString(data.postalCode);
  }
  if ('country' in data && data.country !== undefined) {
    request.country = normalizeString(data.country);
  }
  if ('maritalStatus' in data && data.maritalStatus !== undefined) {
    request.maritalStatus = data.maritalStatus;
  }
  if ('status' in data && data.status !== undefined) {
    request.status = data.status;
  }
  if ('organizationId' in data && data.organizationId !== undefined) {
    request.organizationId = data.organizationId;
  }
  if ('organizationName' in data && data.organizationName !== undefined) {
    request.organizationName = data.organizationName;
  }

  return request;
}

/**
 * Patient API Service
 *
 * Provides methods for managing patient records including:
 * - List patients with pagination and search
 * - Get patient details
 * - Create new patient
 * - Update patient information
 * - Delete patient
 *
 * All methods require authentication and return Promise.
 */
export const patientsApi = {
  /**
   * Get paginated list of patients with optional search/filter
   *
   * @param params - Search and pagination parameters
   * @returns Promise with paginated patient list
   *
   * @example
   * ```tsx
   * // Get first page
   * const { patients, pagination } = await patientsApi.list({ page: 1, limit: 20 });
   *
   * // Search patients
   * const results = await patientsApi.list({ query: 'John', page: 1 });
   *
   * // Filter by gender
   * const results = await patientsApi.list({ gender: 'M', page: 1 });
   * ```
   */
  list: async (params: PatientSearchParams = {}): Promise<PaginatedPatientsResponse> => {
    const { page = 1, limit = 20, query, gender, status, sortBy, sortOrder, patientIds } = params;
    const offset = (page - 1) * limit;

    const result = await medplumResourcesApi.listPatients({
      count: limit,
      offset,
      query,
      gender,
      status,
      sortBy,
      sortOrder,
      patientIds,
    });

    const patients: PatientListItem[] = result.patients.map((resource) =>
      medplumResourcesApi.toPatientListItem(resource)
    );

    const totalPages = result.total > 0 ? Math.ceil(result.total / limit) : 0;

    return {
      patients,
      pagination: {
        total: result.total,
        page,
        limit,
        totalPages,
        hasNext: result.hasNext,
        hasPrev: result.hasPrev,
      },
    };
  },

  /**
   * Get detailed information for a single patient
   *
   * @param uuid - Patient UUID
   * @returns Promise with patient details
   *
   * @example
   * ```tsx
   * const { patient } = await patientsApi.getById('uuid-123');
   * logger.log(patient.name, patient.age);
   * ```
   */
  getById: async (uuid: string): Promise<PatientDetailResponse> => {
    const resource = await medplumResourcesApi.getPatient(uuid);
    const patient = medplumResourcesApi.toPatientDemographics(resource);
    return { patient };
  },

  /**
   * Update existing patient information
   *
   * @param uuid - Patient UUID
   * @param data - Partial patient data to update
   * @returns Promise with updated patient
   *
   * @example
   * ```tsx
   * const { patient } = await patientsApi.update('uuid-123', {
   *   phoneNumber: '+0987654321',
   *   email: 'newemail@example.com'
   * });
   * ```
   */
  update: async (
    uuid: string,
    data: PatientUpdateData | PatientRegistrationData
  ): Promise<PatientUpdateResponse> => {
    const request = buildUpdatePatientRequest(data);
    const resource = await medplumResourcesApi.updatePatient(uuid, request);
    const patient = medplumResourcesApi.toPatientDemographics(resource);

    return {
      patient,
      message: 'Patient updated successfully',
    };
  },

  /**
   * Delete a patient record
   *
   * @param uuid - Patient UUID
   * @returns Promise with deletion confirmation
   *
   * @example
   * ```tsx
   * const { message } = await patientsApi.delete('uuid-123');
   * logger.log(message); // "Patient deleted successfully"
   * ```
   */
  delete: async (uuid: string): Promise<PatientDeletionResponse> => {
    await medplumResourcesApi.deletePatient(uuid);
    return {
      message: 'Patient deleted successfully',
      deletedUuid: uuid,
    };
  },

  /**
   * Search patients by name or identifier (quick search)
   *
   * @param searchTerm - Search term (name, identifier, phone)
   * @returns Promise with matching patients
   *
   * @example
   * ```tsx
   * const results = await patientsApi.search('John');
   * ```
   */
  search: async (searchTerm: string): Promise<PatientListItem[]> => {
    const result = await medplumResourcesApi.listPatients({ query: searchTerm, count: 10 });
    return result.patients.map((resource) => medplumResourcesApi.toPatientListItem(resource));
  },

  /**
   * Check if patient identifier is available
   *
   * @param identifier - Patient identifier to check
   * @returns Promise<boolean> - true if available
   *
   * @example
   * ```tsx
   * const isAvailable = await patientsApi.checkIdentifier('MRN-12345');
   * if (isAvailable) {
   *   // Identifier can be used
   * }
   * ```
   */
  checkIdentifier: async (identifier: string): Promise<boolean> => {
    const normalized = normalizeString(identifier);
    if (!normalized) {
      return true;
    }

    const matches = await medplumResourcesApi.findPatientsByIdentifier(normalized);
    return matches.length === 0;
  },

  /**
   * Trigger AI-powered patient summary generation (fire-and-forget)
   *
   * This endpoint returns immediately. Summary generation happens in the background
   * and may take 5-30 seconds depending on patient history size.
   * The generated summary is stored in Patient.extension.
   *
   * @param patientId - Patient resource UUID
   * @throws Error if the API call fails
   *
   * @example
   * ```tsx
   * await patientsApi.triggerSummaryGeneration('patient-uuid');
   * ```
   */
  triggerSummaryGeneration: async (patientId: string): Promise<void> => {
    const { useAuthStore } = await import('@/lib/stores/authStore');
    const token = useAuthStore.getState().token;
    if (!token) {
      throw new Error('No authentication token available');
    }

    const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

    const response = await fetch(`${API_BASE_URL}/api/summary/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ patientId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }
  },

  /**
   * Get a full snapshot of the patient's today data in a single BFF call.
   * Returns patient, encounter, appointments, vitals, compositions, and
   * the latest ClinicalImpression from the most recent appointment.
   */
  getToday: async (patientId: string) => {
    const response = await apiClient.get<{
      success: boolean;
      data?: {
        patient: any;
        encounter: any | null;
        appointments: any[];
        vitals: any[];
        compositions: any[];
        clinicalImpression: any | null;
      };
      error?: string;
    }>(`/v2/patient/${patientId}/today`);
    return response.data;
  },
};
